import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    ReactNode
} from 'react'

import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ink' | 'ghost'
export type ButtonSize = 'md' | 'lg' | 'icon'

interface ButtonStyleProps {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
    secondary: 'border border-foreground/25 text-foreground hover:border-foreground/70 hover:bg-foreground/[0.04]',
    ink: 'bg-ink text-chalk hover:bg-ink/88',
    ghost: 'text-foreground hover:bg-foreground/[0.06]'
}

const SIZES: Record<ButtonSize, string> = {
    md: 'h-12 px-5 text-[0.9375rem]',
    lg: 'h-14 px-6 text-base',
    icon: 'size-12'
}

export const buttonClasses = ({ variant = 'primary', size = 'md', className }: ButtonStyleProps = {}): string => {
    return cn(
        'inline-flex shrink-0 select-none items-center justify-center gap-2.5 rounded-md font-semibold whitespace-nowrap',
        'transition-[background-color,border-color,color,transform] duration-150 ease-out active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

type ButtonLinkProps = ButtonStyleProps & AnchorHTMLAttributes<HTMLAnchorElement> & {
    children: ReactNode
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant, size, className, children, ...props }): ReactNode => {
    return (
        <a className={buttonClasses({ variant, size, className })} {...props}>
            {children}
        </a>
    )
}

type ButtonProps = ButtonStyleProps & ButtonHTMLAttributes<HTMLButtonElement> & {
    children: ReactNode
}

export const Button: FC<ButtonProps> = ({ variant, size, className, children, type = 'button', ...props }): ReactNode => {
    return (
        <button type={type} className={buttonClasses({ variant, size, className })} {...props}>
            {children}
        </button>
    )
}
